import { useTranslate } from '@tolgee/react';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { relocatePublishButtons } from '../../utils/uploadMediaFile';
import {
  FACEBOOK,
  PLATFORM_LIST,
  PLATFORM_LIST_NAMES
} from '../../../../config/constants';
import { useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import { PLATFORM_COLORS, PLATFORM_ICONS } from '../../../../config/icons';

import SuccessToast from '../../cards/toast';
import { AddImageButton, EditorHeader } from '../sharedComponents';
import toast from 'react-hot-toast';
import { IMAGE, VIDEO } from '../../config/constants';
import MediaViewer from './viewer';
import { cropImage } from '../../../../utils/media/cropImage';

interface Props {
  handleClose: Function;
  currentPlatform: string;
  currentIndex: any;
}

const MediaView: FC<Props> = ({
  handleClose,
  currentPlatform,
  currentIndex
}) => {
  const { t } = useTranslate();
  const footerRef = useRef(null);
  const fileInputRef = useRef(null);

  const [rightMargin, setRightMargin] = useState(0);
  const [startSubmission, setStartSubmission] = useState(false);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const [isProcessingMedia, setIsProcessingMedia] = useState(true);

  const triggerSuccessToast = (type) => {
    toast.success(
      type === IMAGE
        ? t('lomavisCreator.image.saved')
        : t('lomavisCreator.video.saved'),
      {
        position: 'top-right',
        id: 'accounts.invites.table.invite_deleted'
      }
    );
  };

  const platformEnabled = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.platformEnabled
  );
  const allEnabledPlatforms = PLATFORM_LIST.filter(
    (item) => platformEnabled[item] === true
  );
  const [selectedPlatform, setSelectedPlatform] = useState(
    currentPlatform ? currentPlatform : allEnabledPlatforms[0]
  );
  const postDataMediaFiles = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[selectedPlatform]?.media
  );
  const postDataMediaCropList = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[selectedPlatform]?.cropList
  );

  const mediaFiles = postDataMediaFiles
    ?.filter(
      (file) =>
        file.media_type === 'image' ||
        file.media_type === 'video' ||
        file.media_type === 'doc'
    )
    .map((file: any) => {
      return {
        id: `${file.id}`,
        uuid: file.uuid,
        name: file.name,
        width: file.width,
        preview_image: file.preview_image,
        orientation: file.orientation,
        media_type: file.media_type,
        video_duration: file.video_duration,
        uploadPreset: file.uploadPreset,
        file: file.file,
        file_type: file.file_type,
        uploaded: file.uploaded,
        assetBlob: file.assetBlob,
        editorUpdateState: file.editorUpdateState,
        source_media_uuid: file.source_media_uuid,
        key: `${new Date()}`,
        previewImageURL: file.previewImageURL,
        thumbnail_image: file.thumbnail_image,
        original_asset: file.original_asset
      };
    });

  const [updatedMediaFiles, setUpdatedMediaFiles] = useState(mediaFiles);

  const [selectedIndex, setSelectedIndex] = useState(currentIndex || 0);

  useEffect(() => {
    (async () => {
      setIsProcessingMedia(true);
      const updatedMediaFiles = [...mediaFiles]; // clone

      for (const [index, mediaFile] of updatedMediaFiles.entries()) {
        const crop = (postDataMediaCropList || []).find(
          (crop) => crop.uuid === mediaFile?.uuid
        );

        if (crop) {
          const image = new Image();
          const imageSource =
            mediaFile?.assetBlob ||
            mediaFile?.preview_image ||
            mediaFile?.previewImageURL ||
            mediaFile?.original_asset;
          const objectUrl =
            imageSource instanceof Blob
              ? URL.createObjectURL(imageSource)
              : undefined;
          image.src = objectUrl || imageSource;

          await new Promise((resolve) => {
            image.onload = resolve;
            image.onerror = resolve;
          });
          if (objectUrl) URL.revokeObjectURL(objectUrl);

          const imageWidth = image.width;
          const scaleFactor = imageWidth / mediaFile.width;

          const newCrop = {
            x: crop.x * scaleFactor,
            y: crop.y * scaleFactor,
            width: crop.width * scaleFactor,
            height: crop.height * scaleFactor
          };

          const croppedImageUrl = await cropImage(
            mediaFile.original_asset,
            newCrop
          );

          // Replace values in the cloned array

          updatedMediaFiles[index] = {
            ...mediaFile,
            assetBlob: croppedImageUrl,
            preview_image: croppedImageUrl,
            original_asset: croppedImageUrl
          };
        }
      }

      // Dispatch updated array back
      setUpdatedMediaFiles(updatedMediaFiles);
      setIsProcessingMedia(false);
    })();
  }, [postDataMediaFiles]);

  useEffect(() => {
    relocatePublishButtons({
      footerRef: footerRef,
      setRightMargin: setRightMargin
    });
  }, []);

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      // Skip the first render
      hasMounted.current = true;
      return;
    }

    // Only run this on subsequent changes
    setSelectedIndex(0);
  }, [selectedPlatform]);

  return (
    <div className="d-flex flex-column w-100 h-100 gap-4">
      <SuccessToast show={showToast} onClose={() => setShowToast(false)} />

      <EditorHeader>
        <div
          style={{}}
          className="d-flex justify-content-between align-items-center flex-fill h-100 main_editor"
        >
          <Dropdown
            onClick={() => setShowPlatformDropdown(!showPlatformDropdown)}
            show={showPlatformDropdown}
            className=""
          >
            <div
              id="dropdown-basic"
              className={
                'bg-white d-flex align-items-center dropdown-toggle cursor-pointer'
              }
              style={{}}
            >
              <i
                className={PLATFORM_ICONS[selectedPlatform] + ' fs-1 me-2'}
                style={{ color: PLATFORM_COLORS[selectedPlatform] }}
              ></i>

              <div className="fs-3 text-dark">
                {PLATFORM_LIST_NAMES?.[selectedPlatform]}
              </div>
            </div>

            <Dropdown.Menu className="bg-white">
              {allEnabledPlatforms.map((platform, index) => (
                <Dropdown.Item
                  key={index}
                  id={platform + 'key'}
                  onClick={() => setSelectedPlatform(platform)}
                  className="w-100 d-flex justify-content-start align-items-center gap-2 bg-white"
                >
                  <i
                    className={PLATFORM_ICONS[platform] + ' fs-2'}
                    style={{ color: PLATFORM_COLORS[platform] }}
                  ></i>
                  <div className="fs-3 text-dark">
                    {PLATFORM_LIST_NAMES?.[platform]}
                  </div>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </EditorHeader>

      <div
        key={refetchKey}
        style={{ height: 'calc(100% - 60px)' }}
        className="d-flex w-100 flex-column flex-grow-1"
      >
        <MediaViewer
          mediaFiles={updatedMediaFiles}
          setSelectedIndex={setSelectedIndex}
          selectedIndex={selectedIndex}
          startSubmission={startSubmission}
          footerRef={footerRef}
          rightMargin={rightMargin}
          handleClose={handleClose}
          mediaType={updatedMediaFiles[selectedIndex]?.media_type}
        />
      </div>
    </div>
  );
};

export default MediaView;
